import { BaseAction } from "../../../../core/actions/BaseAction";
import { getRecoilStateValue, updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IAdditionalPassenger, IPassenger } from "../../../../Orchestrator/Types/Index";
import { SyncCustomerPassenger } from "../../../../Orchestrator/Types/Types/Passenger/PassengerType";

export class SyncContactPassengerDataAction extends BaseAction<void, SyncCustomerPassenger> {

    sanitizeName = (
        value: string | Date | null,
        field: string
    ): string | Date | null => {
        if (
            (field === "firstName" || field === "lastName") &&
            typeof value === "string"
        ) {
            // return value.replace(/[^A-Za-zÀ-ÿ\s-]/g, "");
            const sanitized = value.replace(/[^\p{L}\p{M}\s-]/gu, "");

            return sanitized.length > 50 ? sanitized.slice(0, 50) : sanitized;
        }
        return value;
    };

    override async run(): Promise<void | null> {
        try {
            const { field, value, passengerId, passengers, customerContact, userData, isFirstPassenger, addonsPassengerList, confirmAddonsPassengerList } = this.params!;
            const processedValue = this.sanitizeName(value, field as string);

            // Get the latest passengers to ensure we have the most up-to-date state
            let latestPassengers = getRecoilStateValue<IPassenger[]>("passengerStateAtom") || passengers;
            const isMultiBooking = getRecoilStateValue<boolean>("isMultiBookingStateAtom");
            const savedPassengerDetail = getRecoilStateValue<IAdditionalPassenger[]>("savedPassengerDetailAtom") || [];
            
            // For multi-booking with no saved passengers, automatically populate email/phoneNumber from customerContact
            // if passengers are missing them (this handles the initial load case)
            if (isMultiBooking && savedPassengerDetail.length === 0 && customerContact) {
                const needsEmailUpdate = customerContact.email && latestPassengers.some((p: IPassenger) => !p.detail?.email);
                const needsPhoneUpdate = customerContact.phoneNumber && latestPassengers.some((p: IPassenger) => !p.detail?.phoneNumber);
                
                if (needsEmailUpdate || needsPhoneUpdate) {
                    // Update all passengers with email/phoneNumber from customerContact if they're missing
                    const passengersWithContact = latestPassengers.map((passenger: IPassenger) => {
                        const updatedDetail = { ...passenger.detail };
                        
                        if (needsEmailUpdate && !updatedDetail.email && customerContact.email) {
                            updatedDetail.email = customerContact.email;
                        }
                        if (needsPhoneUpdate && !updatedDetail.phoneNumber && customerContact.phoneNumber) {
                            updatedDetail.phoneNumber = customerContact.phoneNumber;
                        }
                        
                        return {
                            ...passenger,
                            detail: updatedDetail,
                        };
                    });
                    // Update the state with the populated email/phoneNumber
                    updateRecoilState("passengerStateAtom", passengersWithContact);
                    // Update latestPassengers reference for subsequent processing
                    latestPassengers = passengersWithContact;
                }
            }

            // Update passenger state
            let updatedPassengers = latestPassengers.map((passenger) =>
                passenger.id === passengerId
                    ? field === "dateOfBirth"
                        ? { ...passenger, dateOfBirth: processedValue as Date }
                        : {
                            ...passenger,
                            detail: { ...passenger.detail, [field]: processedValue },
                        }
                    : passenger
            );
            
            // For multi-booking, sync email/phoneNumber to all passengers
            if (isMultiBooking && (field === "email" || field === "phoneNumber")) {
                const updatedValue = typeof processedValue === "string" ? processedValue : "";
                
                // Update all passengers with the same email/phoneNumber
                updatedPassengers = updatedPassengers.map((passenger) => ({
                    ...passenger,
                    detail: {
                        ...passenger.detail,
                        email: field === "email" ? updatedValue : passenger.detail.email,
                        phoneNumber: field === "phoneNumber" ? updatedValue : passenger.detail.phoneNumber,
                    },
                }));
            }
            
            // For multi-booking, update savedPassengerDetailAtom when firstName/lastName change
            if (isMultiBooking && (field === "firstName" || field === "lastName") && typeof processedValue === "string") {
                const selectedPassengersPerType = getRecoilStateValue<{ [key: string]: string | null }>("selectedPassengersPerTypeStateAtom") || {};
                const passengerMapping = getRecoilStateValue<Array<{ selectedPassengerTypeIndex: string; savedPassenger: string }>>("passengerMappingAtom") || [];
                const savedPassengerDetail = getRecoilStateValue<IAdditionalPassenger[]>("savedPassengerDetailAtom") || [];
                
                const slotKey = Object.keys(selectedPassengersPerType).find(
                    key => selectedPassengersPerType[key] === passengerId
                );
                
                if (slotKey) {
                    const [typeIndexStr, subIndexStr] = slotKey.split("-");
                    const typeIndex = parseInt(typeIndexStr, 10);
                    const subIndex = parseInt(subIndexStr, 10);
                    const passengerTypeIndex = typeIndex * 10 + subIndex;
                    
                    const mappingEntry = passengerMapping.find(
                        entry => entry.selectedPassengerTypeIndex === String(passengerTypeIndex)
                    );
                    
                    if (mappingEntry && mappingEntry.savedPassenger) {
                        const updatedSavedPassengerDetail = savedPassengerDetail.map((savedP) => {
                            if (savedP.id === mappingEntry.savedPassenger) {
                                return {
                                    ...savedP,
                                    [field]: processedValue,
                                };
                            }
                            return savedP;
                        });
                        
                        updateRecoilState("savedPassengerDetailAtom", updatedSavedPassengerDetail);
                    }
                }
            }
            
            updateRecoilState("passengerStateAtom", updatedPassengers);

            /**
             * This is for to update passenger name for addons passenger
            */
            const updateAddonsPassenger = addonsPassengerList?.map(({ ...leg }) => {
                if (leg?.addonsPassenger) {
                    const confirmLeg = confirmAddonsPassengerList?.find(d => d.legId === leg.legId)
                    leg.addonsPassenger = leg.addonsPassenger.map(({ ...p }, index) => {
                        const confirmPass = confirmLeg?.addonsPassenger?.find(d => d.passengerId === p.passengerId)
                        if (p.passengerId === passengerId) {
                            const pass = updatedPassengers.find(d => d.id === passengerId)
                            p = {
                                ...p,
                                passengerName: `${pass?.detail?.firstName || ""} ${pass?.detail?.lastName || ""}`,
                                isMePassenger: (userData?.firstName === pass?.detail?.firstName && userData?.lastName === pass?.detail?.lastName)
                            }
                        }
                        if (confirmPass) {
                            p.addonItem = confirmPass?.addonItem || {}
                        }
                        return p
                    })
                }
                return leg
            })
            if ((updateAddonsPassenger || []).length > 0) {

                updateRecoilState("addonsPassengerListAtom", updateAddonsPassenger)
                updateRecoilState("confirmAddonsPassengerListAtom", updateAddonsPassenger)
            }

            // Immediately update customer contact if it's the first passenger
            if (field !== "dateOfBirth" && typeof processedValue === "string") {
                if (isFirstPassenger && (!userData?.firstName || userData.firstName === "-" || userData.firstName === "" ||
                    !userData.lastName || userData.lastName === "-" || userData.lastName === "")) {
                    const currentPassenger = updatedPassengers.find((p) => p.id === passengerId);
                    if (currentPassenger) {
                        const updatedCustomerContact = {
                            ...customerContact,
                            firstName: field === "firstName" ? processedValue : currentPassenger.detail.firstName,
                            lastName: field === "lastName" ? processedValue : currentPassenger.detail.lastName,
                            email: currentPassenger.detail.email || customerContact.email
                        };
                        updateRecoilState("customerContactStateAtom", updatedCustomerContact);
                    }
                }
            }
        } catch (error) {
            console.error("Error in SyncContactPassengerDataAction:", error);
        }
    }
}

// Create a singleton instance
export const syncContactPassengerDataAction = new SyncContactPassengerDataAction(); 